import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import TemporaryApiKeyService from 'services/TemporaryApiKeyService';
import type { ServerMessage } from '../protocol';
import type { ChatItem } from '../reducer';
import { credentialsErrorText } from '../errorCopy';
import { selectChatPhase, useLoadingReveal } from '../PiChatSkeleton';
import { createTemporaryCredentials } from '../temporaryCredentials';
import {
  usePiChat,
  type PiChatFrameMeta,
  type PiChatStart,
} from '../usePiChat';
import { useVersions } from '../versions/useVersions';
import {
  selectRedoTarget,
  selectUndoTarget,
} from '../versions/versionsReducer';

export interface UseBuilderChatOptions {
  filesWarning: string;
  onServerMessage: (message: ServerMessage, meta: PiChatFrameMeta) => void;
  getStart: () => PiChatStart;
  onConnect?: () => void;
}

export const useBuilderChat = (options: UseBuilderChatOptions) => {
  const { t } = useTranslation();
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const itemsRef = useRef<readonly ChatItem[]>([]);
  const {
    versions,
    versionsRef,
    restoreEvents,
    handleServerMessage: handleVersionsMessage,
    reset: resetVersions,
    restore,
  } = useVersions({
    getItemCount: () => itemsRef.current.length,
    onUnknownRestore: () =>
      socket.addNotice({ code: 'unknown_snapshot_restored' }),
    filesWarning: options.filesWarning,
  });

  const [credentials] = useState(() =>
    createTemporaryCredentials({
      fetchKey: () => TemporaryApiKeyService.createTemporaryApiKey(),
    })
  );

  const socket = usePiChat({
    getCredentials: credentials.get,
    credentialsErrorNotice: credentialsErrorText(t),
    isSendBlocked: () => versionsRef.current.restoringSnapshotId !== null,
    onServerMessage: (message, meta) => {
      handleVersionsMessage(message, meta);
      options.onServerMessage(message, meta);
    },
  });
  const { connectionState, restoreSnapshot: sendRestore } = socket;
  itemsRef.current = socket.items;
  const panelState = { ...socket, ...versions };

  const connectImpl = () => {
    socket.connect(optionsRef.current.getStart);
    resetVersions();
    optionsRef.current.onConnect?.();
  };
  const connectRef = useRef(connectImpl);
  connectRef.current = connectImpl;
  const connect = useCallback(() => connectRef.current(), []);

  useEffect(() => {
    connectRef.current();
  }, []);

  const undo = () => {
    const target = selectUndoTarget(versions);
    if (target) void restore(target.id, sendRestore);
  };

  const redo = () => {
    const target = selectRedoTarget(versions);
    if (target) void restore(target.id, sendRestore);
  };

  const chatPhase =
    socket.reconnecting || connectionState === 'idle'
      ? 'ready'
      : selectChatPhase(connectionState);
  const isLoading = chatPhase === 'loading';
  const loadingRevealed = useLoadingReveal(isLoading);

  return {
    socket,
    versions,
    restoreEvents,
    panelState,
    connect,
    undo,
    redo,
    chatPhase,
    isLoading,
    loadingRevealed,
  };
};

export type BuilderChat = ReturnType<typeof useBuilderChat>;
